import { useState, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { fetchMetaAdsOverview, fetchMetaAdsCampaigns } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer, ComposedChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  Megaphone, Eye, Users, DollarSign, MousePointerClick, TrendingUp, Target,
  Facebook, Instagram, Activity, Percent, Wallet, BarChart3, Filter,
} from "lucide-react";

const RANGE_OPTIONS = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
  { value: 90, label: "90 días" },
  { value: 365, label: "12 meses" },
];

function getDateRange(days) {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - days);
  const iso = (d) => d.toISOString().split("T")[0];
  return { date_from: iso(from), date_to: iso(today) };
}

const fmt = (n) => (n == null ? "0" : Number(n).toLocaleString("es-AR"));
const fmtMoney = (n) =>
  n == null ? "$0,00" : "$" + Number(n).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoneyShort = (n) => {
  if (n == null) return "$0";
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return "$" + (v / 1_000_000).toLocaleString("es-AR", { maximumFractionDigits: 2 }) + "M";
  if (Math.abs(v) >= 1_000) return "$" + (v / 1_000).toLocaleString("es-AR", { maximumFractionDigits: 1 }) + "k";
  return fmtMoney(v);
};
const fmtPct = (n) => (n == null ? "0%" : `${Number(n).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`);
const fmtDec = (n, max = 2) =>
  n == null ? "0" : Number(n).toLocaleString("es-AR", { maximumFractionDigits: max });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function MetaAdsSection({ clientId }) {
  const [rangeDays, setRangeDays] = useState(30);
  const [innerTab, setInnerTab] = useState("cuenta");
  const [providerFilter, setProviderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const range = useMemo(() => getDateRange(rangeDays), [rangeDays]);

  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ["meta-ads-overview", clientId, range.date_from, range.date_to],
    queryFn: () => fetchMetaAdsOverview(clientId, range.date_from, range.date_to),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: campaignsData, isLoading: loadingCampaigns } = useQuery({
    queryKey: ["meta-ads-campaigns", clientId, range.date_from, range.date_to, providerFilter, statusFilter],
    queryFn: () => fetchMetaAdsCampaigns(clientId, range.date_from, range.date_to, providerFilter, statusFilter),
    enabled: !!overview?.configured && innerTab === "campanas",
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const primaryChannel = overview?.channels?.[0];
  const chartData = useMemo(() => {
    const tl = primaryChannel?.timelines || {};
    const spend = tl.spend || [];
    const clicks = tl.clicks || [];
    const impressions = tl.impressions || [];
    const byDate = {};
    spend.forEach((p) => {
      byDate[p.date] = byDate[p.date] || { date: p.date };
      byDate[p.date].spend = p.value;
    });
    clicks.forEach((p) => {
      byDate[p.date] = byDate[p.date] || { date: p.date };
      byDate[p.date].clicks = p.value;
    });
    impressions.forEach((p) => {
      byDate[p.date] = byDate[p.date] || { date: p.date };
      byDate[p.date].impressions = p.value;
    });
    return Object.values(byDate).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [primaryChannel]);

  if (loadingOverview) {
    return (
      <div className="space-y-4" data-testid="meta-ads-loading">
        <div className="h-24 rounded-md skeleton" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-md skeleton" />)}
        </div>
        <div className="h-72 rounded-md skeleton" />
      </div>
    );
  }

  if (!overview?.configured) {
    return (
      <div
        className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center"
        data-testid="meta-ads-not-configured"
      >
        <Megaphone className="h-10 w-10 text-slate-400 mx-auto mb-3" />
        <h3 className="font-display text-base font-semibold text-slate-900">Sin campañas Meta Ads</h3>
        <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
          {overview?.message || "Este cliente no tiene campañas de Facebook/Instagram Ads activas en el período."}
        </p>
        <p className="text-[11px] text-slate-400 mt-2">Probá un rango mayor o conectá la cuenta publicitaria en Metricool.</p>
      </div>
    );
  }

  const totals = overview.totals || {};
  const channels = overview.channels || [];

  return (
    <div className="space-y-5" data-testid="meta-ads-section">
      {/* Header + range */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 border border-purple-200 flex items-center justify-center shadow-sm">
            <Megaphone className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900 leading-tight">Meta Ads</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Facebook + Instagram · {channels.length} {channels.length === 1 ? "cuenta" : "cuentas"} · vía Metricool
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5" data-testid="meta-ads-range-selector">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setRangeDays(opt.value)}
              data-testid={`meta-ads-range-${opt.value}`}
              className={`px-2.5 py-1 text-xs font-medium rounded-sm transition ${
                rangeDays === opt.value ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <Tabs value={innerTab} onValueChange={setInnerTab} className="w-full">
        <TabsList className="h-auto p-1 bg-slate-100 border border-slate-200 rounded-md inline-flex gap-1">
          <TabsTrigger value="cuenta" data-testid="meta-ads-tab-cuenta"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
            <Target className="h-3.5 w-3.5" /> Cuenta
          </TabsTrigger>
          <TabsTrigger value="campanas" data-testid="meta-ads-tab-campanas"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" /> Campañas
          </TabsTrigger>
        </TabsList>

        {/* CUENTA */}
        <TabsContent value="cuenta" className="mt-4 space-y-4">
          {/* Alcance */}
          <section>
            <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Alcance e inversión</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2.5" data-testid="meta-ads-reach-kpis">
              <KpiCard icon={Users} label="Alcance total" value={fmt(totals.reach)} tone="blue" testid="meta-ads-kpi-reach" />
              <KpiCard icon={Eye} label="Impresiones" value={fmt(totals.impressions)} tone="indigo" testid="meta-ads-kpi-impressions" />
              <KpiCard icon={Wallet} label="Gasto total" value={fmtMoneyShort(totals.spend)} tone="emerald" testid="meta-ads-kpi-spend" />
            </div>
          </section>

          {/* Resultados */}
          <section>
            <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Resultados</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="meta-ads-results-kpis">
              <KpiCard icon={MousePointerClick} label="Clics" value={fmt(totals.clicks)} tone="sky" testid="meta-ads-kpi-clicks" />
              <KpiCard icon={DollarSign} label="Valor de conversión" value={fmtMoneyShort(totals.conversions_value)} tone="emerald" testid="meta-ads-kpi-conversions-value" />
              <KpiCard icon={TrendingUp} label="ROAS" value={fmtDec(totals.purchase_roas)} tone="amber" testid="meta-ads-kpi-roas" />
              <KpiCard icon={Activity} label="Gasto" value={fmtMoneyShort(totals.spend)} tone="rose" testid="meta-ads-kpi-spend-2" />
            </div>
          </section>

          {/* Rendimiento */}
          <section>
            <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Rendimiento promedio</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="meta-ads-perf-kpis">
              <KpiCard icon={DollarSign} label="CPM" value={fmtMoney(totals.cpm)} tone="purple" testid="meta-ads-kpi-cpm" />
              <KpiCard icon={DollarSign} label="CPC" value={fmtMoney(totals.cpc)} tone="indigo" testid="meta-ads-kpi-cpc" />
              <KpiCard icon={Percent} label="CTR" value={fmtPct(totals.ctr)} tone="emerald" testid="meta-ads-kpi-ctr" />
              <KpiCard icon={Wallet} label="Inversión total" value={fmtMoneyShort(totals.spend)} tone="rose" testid="meta-ads-kpi-spend-3" />
            </div>
          </section>

          {/* Evolution chart */}
          {chartData.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="meta-ads-evolution-chart">
              <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">
                Evolución diaria {primaryChannel ? `· ${primaryChannel.label}` : ""}
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData}>
                  <defs>
                    <linearGradient id="metaSpendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={fmtDate} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "none", borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: "#e2e8f0" }}
                    itemStyle={{ color: "#fff" }}
                    formatter={(value, name) => {
                      if (name === "Gasto") return [fmtMoneyShort(value), name];
                      return [fmt(value), name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area yAxisId="left" type="monotone" dataKey="spend" name="Gasto" stroke="#10b981" fill="url(#metaSpendGrad)" strokeWidth={2} />
                  <Bar yAxisId="right" dataKey="clicks" name="Clics" fill="#6366f1" opacity={0.6} radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Per-channel cards */}
          {channels.length > 1 && (
            <section data-testid="meta-ads-channels-grid">
              <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Por cuenta publicitaria</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {channels.map((ch) => (
                  <div key={ch.blog_id} className="bg-white border border-slate-200 rounded-md p-3">
                    <div className="flex items-center gap-2 mb-2">
                      {ch.picture && <img src={ch.picture} alt="" className="w-7 h-7 rounded-full object-cover" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900 truncate">{ch.label}</div>
                        <div className="text-[10px] text-slate-500">Cuenta publicitaria</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-base font-semibold text-slate-900 tabular-nums">{fmtMoneyShort(ch.spend)}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Gasto</div>
                      </div>
                      <div>
                        <div className="text-base font-semibold text-slate-900 tabular-nums">{fmt(ch.impressions)}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Impr.</div>
                      </div>
                      <div>
                        <div className="text-base font-semibold text-slate-900 tabular-nums">{fmt(ch.clicks)}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Clics</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </TabsContent>

        {/* CAMPAÑAS */}
        <TabsContent value="campanas" className="mt-4 space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="meta-ads-filters">
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <Filter className="h-3.5 w-3.5" /> Filtrar:
            </div>
            <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5">
              <FilterButton
                active={providerFilter === ""}
                onClick={() => setProviderFilter("")}
                testid="meta-ads-prov-all"
              >Todos</FilterButton>
              <FilterButton
                active={providerFilter === "facebook"}
                onClick={() => setProviderFilter("facebook")}
                testid="meta-ads-prov-facebook"
              ><Facebook className="h-3 w-3 inline mr-1" />Facebook</FilterButton>
              <FilterButton
                active={providerFilter === "instagram"}
                onClick={() => setProviderFilter("instagram")}
                testid="meta-ads-prov-instagram"
              ><Instagram className="h-3 w-3 inline mr-1" />Instagram</FilterButton>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5">
              <FilterButton
                active={statusFilter === ""}
                onClick={() => setStatusFilter("")}
                testid="meta-ads-status-all"
              >Cualquier estado</FilterButton>
              <FilterButton
                active={statusFilter === "ACTIVE"}
                onClick={() => setStatusFilter("ACTIVE")}
                testid="meta-ads-status-active"
              >Activas</FilterButton>
              <FilterButton
                active={statusFilter === "PAUSED"}
                onClick={() => setStatusFilter("PAUSED")}
                testid="meta-ads-status-paused"
              >Pausadas</FilterButton>
            </div>
          </div>

          {loadingCampaigns ? (
            <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-12 rounded-md skeleton" />)}</div>
          ) : (
            <CampaignsTable data={campaignsData} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ─────────────── Subcomponents ─────────────── */

function KpiCard({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    blue: { bg: "bg-blue-50", border: "border-blue-100", text: "text-blue-600" },
    indigo: { bg: "bg-indigo-50", border: "border-indigo-100", text: "text-indigo-600" },
    sky: { bg: "bg-sky-50", border: "border-sky-100", text: "text-sky-600" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-100", text: "text-emerald-600" },
    rose: { bg: "bg-rose-50", border: "border-rose-100", text: "text-rose-600" },
    amber: { bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-600" },
    purple: { bg: "bg-purple-50", border: "border-purple-100", text: "text-purple-600" },
    slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testid}>
      <div className={`w-7 h-7 rounded-md ${t.bg} border ${t.border} flex items-center justify-center mb-1.5`}>
        <Icon className={`h-3.5 w-3.5 ${t.text}`} />
      </div>
      <div className="text-lg font-display font-semibold text-slate-900 leading-tight tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function FilterButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 text-xs font-medium rounded-sm transition ${
        active ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function CampaignsTable({ data }) {
  const [sortBy, setSortBy] = useState("spent");
  const campaigns = data?.campaigns || [];
  const totals = data?.totals || {};
  const sorted = useMemo(
    () => [...campaigns].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0)),
    [campaigns, sortBy]
  );

  if (campaigns.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center" data-testid="meta-ads-campaigns-empty">
        <BarChart3 className="h-9 w-9 text-slate-400 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Sin campañas en el período / filtros seleccionados.</p>
      </div>
    );
  }

  return (
    <>
      {/* Summary mini-cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="meta-ads-campaigns-summary">
        <SmallStat label="Campañas" value={fmt(totals.count)} />
        <SmallStat label="Gasto" value={fmtMoneyShort(totals.spent)} />
        <SmallStat label="Impresiones" value={fmt(totals.impressions)} />
        <SmallStat label="Clics" value={fmt(totals.clicks)} />
        <SmallStat label="Conversiones" value={fmt(totals.conversions)} />
        <SmallStat label="FB / IG" value={`${totals.providers?.facebook || 0} / ${totals.providers?.instagram || 0}`} />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Lista de campañas</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{campaigns.length} campañas · ordenadas por {sortBy}</p>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-md p-0.5">
            {[
              { v: "spent", l: "Gasto" },
              { v: "impressions", l: "Impr." },
              { v: "clicks", l: "Clics" },
              { v: "conversions", l: "Conv." },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setSortBy(opt.v)}
                data-testid={`meta-ads-sort-${opt.v}`}
                className={`px-2.5 py-1 text-xs font-medium rounded-sm transition ${
                  sortBy === opt.v ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="meta-ads-campaigns-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5 font-medium">Campaña</th>
                <th className="px-3 py-2.5 font-medium">Plataforma</th>
                <th className="px-3 py-2.5 font-medium">Estado</th>
                <th className="px-3 py-2.5 font-medium text-right">Alcance</th>
                <th className="px-3 py-2.5 font-medium text-right">Impr.</th>
                <th className="px-3 py-2.5 font-medium text-right">Clics</th>
                <th className="px-3 py-2.5 font-medium text-right">Conv.</th>
                <th className="px-3 py-2.5 font-medium text-right">Resultados</th>
                <th className="px-3 py-2.5 font-medium text-right">CPM</th>
                <th className="px-3 py-2.5 font-medium text-right">CPC</th>
                <th className="px-3 py-2.5 font-medium text-right">CTR</th>
                <th className="px-3 py-2.5 font-medium text-right">ROAS</th>
                <th className="px-3 py-2.5 font-medium text-right">Gasto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`meta-ads-campaign-row-${c.id}`}>
                  <td className="px-4 py-2.5 max-w-[260px]">
                    <div className="text-slate-900 truncate" title={c.name}>{c.name}</div>
                    {c.objective && (
                      <div className="text-[10px] text-slate-500 truncate">{c.objective}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {c.provider === "facebook" ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        <Facebook className="h-3 w-3" /> Facebook
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-pink-50 text-pink-700 border border-pink-200">
                        <Instagram className="h-3 w-3" /> Instagram
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(c.reach)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(c.impressions)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(c.clicks)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmt(c.conversions)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums whitespace-nowrap">
                    {c.results
                      ? <>
                          <span className="font-semibold">{fmt(c.results)}</span>
                          {c.results_label && <span className="text-[10px] text-slate-500 ml-1">{c.results_label}</span>}
                        </>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmtMoney(c.cpm)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmtMoney(c.cpc)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmtPct(c.ctr)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums">{fmtDec(c.purchase_roas)}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums">{fmtMoneyShort(c.spent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function StatusBadge({ status }) {
  const norm = (status || "").toUpperCase();
  const styles = {
    ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
    PAUSED: "bg-amber-50 text-amber-700 border-amber-200",
    ARCHIVED: "bg-slate-100 text-slate-600 border-slate-200",
    DELETED: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const labels = {
    ACTIVE: "Activa",
    PAUSED: "Pausada",
    ARCHIVED: "Archivada",
    DELETED: "Eliminada",
  };
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border ${styles[norm] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {labels[norm] || status || "—"}
    </span>
  );
}

function SmallStat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-2.5">
      <div className="text-sm font-display font-semibold text-slate-900 tabular-nums leading-tight">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5 truncate">{label}</div>
    </div>
  );
}
